import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, MailCheck, XCircle } from "lucide-react";
import accountApi from "@/lib/accountApiClient";
import { useAccount } from "@/lib/AccountContext";
import { recoveryErrorKind, useRecoveryTokenFromUrl } from "@/lib/accountRecovery";
import { useLang } from "@/lib/i18n";
import useSEO from "@/lib/useSEO";
import { Button } from "@/components/ui/button";

export default function AccountVerifyEmail() {
  const { t } = useLang();
  const { isAuthenticated, refreshMe } = useAccount();
  const token = useRecoveryTokenFromUrl();
  const [state, setState] = useState(() => token ? "working" : "invalid");
  const confirmationRequest = useRef(null);
  const profileRefreshRequested = useRef(false);

  useSEO({
    title: `${t("account.verify.title")} — Mindovia`,
    description: t("account.verify.description"),
    url: null,
    robots: "noindex, nofollow, noarchive",
  });

  useEffect(() => {
    if (!token) return undefined;
    let active = true;
    const confirm = async () => {
      try {
        confirmationRequest.current ||= accountApi.post(
          "/account/email-verification/confirm",
          { token },
        );
        await confirmationRequest.current;
        if (isAuthenticated && !profileRefreshRequested.current) {
          profileRefreshRequested.current = true;
          await refreshMe();
        }
        if (active) setState("success");
      } catch (error) {
        if (active) setState(recoveryErrorKind(error));
      }
    };
    confirm();
    return () => { active = false; };
  }, [isAuthenticated, refreshMe, token]);

  const success = state === "success";
  const working = state === "working";
  return (
    <div className="min-h-[70vh] px-4 py-16" data-wrap="responsive">
      <section className="mx-auto max-w-md rounded-3xl border border-border bg-card p-7 text-center shadow-lg sm:p-9" aria-live="polite">
        {success ? <CheckCircle2 className="mx-auto h-12 w-12 text-emerald-600" /> : working ? <MailCheck className="mx-auto h-12 w-12 animate-pulse text-teal-700" /> : <XCircle className="mx-auto h-12 w-12 text-destructive" />}
        <h1 className="mt-5 font-display text-3xl tracking-tight">{t(success ? "account.verify.success_title" : working ? "account.verify.working" : "account.verify.error_title")}</h1>
        <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
          {t(success ? "account.verify.success" : working ? "account.verify.description" : `account.recovery.error.${state}`)}
        </p>
        <Button asChild variant="outline" className="mt-7 rounded-full">
          <Link to={isAuthenticated ? "/account/settings" : "/login"}>{t(isAuthenticated ? "account.settings.title" : "account.signin")}</Link>
        </Button>
      </section>
    </div>
  );
}
